import { Controller } from "@hotwired/stimulus"

// Visual-only drag & drop for kanban cards across phase columns.
// Cards can be picked up and dragged between columns with full visual
// feedback (ghost placeholder, column highlight) but nothing is
// persisted — the card snaps back on drop.
export default class extends Controller {
  // Persist mode (Phelipe 2026-09-03): grouped by SECONDARY phase on the
  // detailed layout, a drop between step columns saves; a drop into a
  // step of another primary phase changes the phase straight away too
  // (2026-10-20), unless it needs a payment or a bid winner (popup).
  // Backwards works too (2026-10-29): Construction → Bidding straight
  // away, Construction → Design after one confirm (it takes the
  // contractors off the team). Grouped by primary phase the drag stays
  // visual-only, as before.
  static values = { persist: Boolean, csrf: String }

  connect() {
    this._onDragStart = this._onDragStart.bind(this)
    this._onDragEnd = this._onDragEnd.bind(this)
    this._onDragOver = this._onDragOver.bind(this)
    this._onDragLeave = this._onDragLeave.bind(this)
    this._onDrop = this._onDrop.bind(this)

    // Re-setup when turbo-frames load new content
    this._observer = new MutationObserver(() => this._setup())
    this._observer.observe(this.element, { childList: true, subtree: true })
    this._setup()
  }

  disconnect() {
    this._observer?.disconnect()
    this._cleanup()
  }

  // ── Setup ────────────────────────────────────────────────────

  _setup() {
    // Make every card item draggable
    this._itemContainers().forEach(container => {
      ;[...container.children].forEach(card => {
        if (card.classList.contains("kanban-placeholder")) return
        if (card.getAttribute("draggable") === "true") return

        card.setAttribute("draggable", "true")
        card.style.cursor = "grab"
        card.addEventListener("dragstart", this._onDragStart)
        card.addEventListener("dragend", this._onDragEnd)

        // Disable native link drag on inner <a> tags so the outer
        // wrapper's drag takes priority (e.g. bidding cards)
        if (card.tagName !== "A") {
          card.querySelectorAll("a[href]").forEach(a => {
            a.setAttribute("draggable", "false")
          })
        }
      })
    })

    // Set up column drop zones (only real phase columns)
    this._columns().forEach(col => {
      if (col._kanbanDropSetup) return
      col._kanbanDropSetup = true
      col.addEventListener("dragover", this._onDragOver)
      col.addEventListener("dragleave", this._onDragLeave)
      col.addEventListener("drop", this._onDrop)
    })
  }

  _cleanup() {
    this._itemContainers().forEach(container => {
      ;[...container.children].forEach(card => {
        card.removeAttribute("draggable")
        card.style.cursor = ""
        card.removeEventListener("dragstart", this._onDragStart)
        card.removeEventListener("dragend", this._onDragEnd)
      })
    })
    this._removePlaceholders()
  }

  // ── Drag events ──────────────────────────────────────────────

  _onDragStart(e) {
    const card = e.currentTarget
    this._draggedCard = card
    this._sourceContainer = card.parentElement
    this._cardHeight = card.offsetHeight

    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", "")

    // Delay so the browser captures the full card as the drag image
    requestAnimationFrame(() => {
      card.style.opacity = "0.35"
      card.style.transform = "scale(0.97)"
    })
  }

  _onDragEnd(e) {
    const card = e.currentTarget

    // Restore card
    card.style.opacity = ""
    card.style.transform = ""

    this._removePlaceholders()

    this._draggedCard = null
    this._sourceContainer = null
    this._cardHeight = 0
  }

  _onDragOver(e) {
    if (!this._draggedCard) return

    e.preventDefault()
    e.dataTransfer.dropEffect = "move"

    const column = e.currentTarget
    const container = column.querySelector('[id^="phase-"][id$="-items"]')
    if (!container) return

    // Only show placeholder in a different column than the source
    if (container === this._sourceContainer) {
      this._removePlaceholderIn(column)
    } else {
      this._ensurePlaceholder(container)
    }
  }

  _onDragLeave(e) {
    const column = e.currentTarget
    if (column.contains(e.relatedTarget)) return

    this._removePlaceholderIn(column)
  }

  async _onDrop(e) {
    e.preventDefault()
    // Visual-only unless persisting: the card snaps back via _onDragEnd
    if (!this.persistValue || !this._draggedCard) return

    const column = e.currentTarget
    const container = column.querySelector('[id^="phase-"][id$="-items"]')
    const card = this._draggedCard
    const source = this._sourceContainer
    if (!container || !source || container === source) return

    const stepId = column.dataset.secondaryPhaseId
    const stepName = column.dataset.secondaryPhaseName || ""
    const projectId = card.dataset.projectId
    if (!stepId || !projectId) return   // the "No secondary phase" bucket isn't a target

    const fromPrimary = card.dataset.primaryPhase
    const toPrimary = column.dataset.primaryPhase
    if (fromPrimary !== toPrimary) {
      // Another primary phase (Phelipe 2026-10-20: no pop-up, it has to be
      // fast). A move that needs nothing from the person runs straight away;
      // one that needs a payment or a bid winner still opens the popup.
      await this._movePrimary({ card, source, container, projectId, fromPrimary, toPrimary, stepId, stepName })
      return
    }

    const ok = await this._assignStep(projectId, stepId)
    if (!ok) return
    this._placeCard(card, source, container)
    this._toast(`${card.dataset.projectName || "Project"} → ${stepName}`)
  }

  async _movePrimary({ card, source, container, projectId, fromPrimary, toPrimary, stepId, stepName }) {
    const name = card.dataset.projectName || "Project"
    let opts
    try {
      const resp = await fetch(`/projects/${projectId}/phase_move_options?to=${encodeURIComponent(toPrimary)}`, {
        headers: { "Accept": "application/json" }, credentials: "same-origin"
      })
      opts = await resp.json()
    } catch {
      this._toast("Couldn't move the card", "alert")
      return
    }

    if (!opts.can_move) {
      this._toast(`Only the project's admins can change ${name}'s phase`, "alert")
      return
    }
    if (opts.kind === "unsupported") {
      this._toast(`${name} can't be moved from ${this._phaseLabel(fromPrimary)} to ${this._phaseLabel(toPrimary)}`, "alert")
      return
    }

    const detail = { projectId, projectName: card.dataset.projectName || "", fromPrimary, toPrimary, stepId, stepName }
    const run = () => this._runPrimary({ card, source, container, opts, toPrimary, stepId, stepName })
    if (opts.kind === "remove_contractors") {
      // Construction → Design: the popup asks once, then runs it here.
      this.dispatch("phaseMove", { detail: { ...detail, run } })
      return
    }

    const instant = opts.kind === "confirm" || (opts.kind === "bidding" && !opts.requires_payment)
    if (!instant) {
      // A payment or a bid winner is needed: the popup takes them to settings.
      this.dispatch("phaseMove", { detail })
      return
    }

    await run()
  }

  async _runPrimary({ card, source, container, opts, toPrimary, stepId, stepName }) {
    const name = card.dataset.projectName || "Project"
    try {
      const resp = await fetch(opts.url, {
        method: opts.method,
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfValue, "Accept": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ ...(opts.params || {}), board_secondary_phase_id: stepId })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || data.error) throw new Error(data.errorMsg || data.error || "Couldn't move the card")
    } catch (err) {
      this._toast(err.message, "alert")
      return
    }

    card.dataset.primaryPhase = toPrimary
    this._placeCard(card, source, container)
    this._toast(`${name} → ${stepName}`)
  }

  _phaseLabel(phase) {
    const labels = { real_estate: "Real Estate", dev: "Design", bidding: "Bidding", construction: "Construction", operations: "Operations" }
    return labels[phase] || phase
  }

  async _assignStep(projectId, stepId) {
    try {
      const resp = await fetch(`/board_cards/${projectId}/secondary_phase`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfValue, "Accept": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ board_secondary_phase_id: stepId })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || data.error) throw new Error(data.error || "Couldn't move the card")
      return true
    } catch (err) {
      this._toast(err.message, "alert")
      return false
    }
  }

  // Move the card into its new column at the top (where the placeholder
  // was) and keep the two column counters honest — no reload.
  _placeCard(card, source, target) {
    const placeholder = target.querySelector(".kanban-placeholder")
    target.insertBefore(card, placeholder || target.firstElementChild)
    placeholder?.remove()
    this._bumpCounter(this._keyOf(source), -1)
    this._bumpCounter(this._keyOf(target), 1)
  }

  _keyOf(container) {
    return container.id.replace(/^phase-/, "").replace(/-items$/, "")
  }

  _bumpCounter(key, delta) {
    const el = document.getElementById(`phase-${key}-count`)
    if (!el) return
    const n = parseInt(el.textContent, 10)
    if (!Number.isNaN(n)) el.textContent = String(Math.max(0, n + delta))
  }

  _toast(message, type = "notice") {
    const div = document.createElement("div")
    div.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    div.textContent = message
    div.setAttribute("data-controller", "flash")
    div.setAttribute("data-flash-dismiss-after-value", "5000")
    document.body.prepend(div)
  }

  // ── Ghost placeholder ──────────────────────────────────────

  _ensurePlaceholder(container) {
    // Remove placeholders from other columns
    this._itemContainers().forEach(c => {
      if (c !== container) c.querySelector(".kanban-placeholder")?.remove()
    })

    let placeholder = container.querySelector(".kanban-placeholder")
    if (!placeholder) {
      placeholder = document.createElement("div")
      placeholder.className = "kanban-placeholder"
      const h = this._cardHeight || 90
      placeholder.style.cssText =
        `height: ${h}px; border: 2px dashed #d1d5db; border-radius: 8px; ` +
        `background: #f9fafb; transition: opacity 0.15s; opacity: 1;`
    }

    // Always insert at the top of the column
    if (container.firstElementChild !== placeholder) {
      container.insertBefore(placeholder, container.firstElementChild)
    }
  }

  _removePlaceholderIn(column) {
    column.querySelector(".kanban-placeholder")?.remove()
  }

  _removePlaceholders() {
    this.element.querySelectorAll(".kanban-placeholder").forEach(el => el.remove())
  }

  // ── Selectors ────────────────────────────────────────────────

  _itemContainers() {
    return this.element.querySelectorAll('[id^="phase-"][id$="-items"]')
  }

  _columns() {
    return this.element.querySelectorAll('[data-infinite-scroll-target="column"]')
  }
}
